import {Component, inject, Input, OnInit} from '@angular/core';
import {DOCUMENT} from '@angular/common';
import {HttpBackend, HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {map, shareReplay} from 'rxjs/operators';

export type ImageSize = {
    width: number,
    height: number,
}

export type LoadedResult = {
    image: SVGImageElement,
    sizeMap: Record<string, ImageSize>,
}

export type InitMapVO = {
    prefix: string,
    result: Observable<LoadedResult>,
}

function parseViewBoxValue($value: string): ImageSize {
    if (!/^\d+ \d+ \d+ \d+$/.test($value)) {
        return null;
    }

    const [x, y, w, h] = $value.split(' ', 4).map($ => parseInt($));
    return {
        width: w - x,
        height: h - y,
    };
}

/**
 * RegExp for `url(link/to/resource.ext)` extraction.
 */
const URL_REGEX = /^url\(("|'|)((?:.(?!\1\)))*.)\1\)$/;

/**
 * Extracts resource path from `url(path/to/resource.ext)` | `url('res.ext')` | `url("name.ext")`
 */
function extractFromUrl($: string): string {
    if (URL_REGEX.test($)) {
        return $.replace(URL_REGEX, '$2');
    }
    return $;
}

@Component({
    standalone: true,
    selector: 'app-sprite-image',
    template: ``,
})
export abstract class SpriteImageBaseComponent implements OnInit {

    protected static readonly loaderMap = new Map<SpriteImageBaseComponent, InitMapVO>();

    protected static counter: number = 0;

    public static initialize($httpService: HttpClient, $document: Document, $idPrefix: string, $imgUrl: string, $class?: string): Observable<LoadedResult> {
        return $httpService.get($imgUrl, {
            responseType: 'text'
        })
            .pipe(
                map($ => {
                    const container = $document.createElement('body');
                    container.innerHTML = $;

                    if (container.childNodes.length === 0 || container.firstChild.nodeName.toLowerCase() !== 'svg') {
                        throw Error('Loaded asset is not an SVG image.');
                    }

                    const image: SVGImageElement = container.firstChild as SVGImageElement;

                    // Prefix all elements with `id="any"`
                    // Also build image size map by id names.
                    const sizeMap: Record<string, ImageSize> = {};

                    const nodesWithId = image.querySelectorAll('[id]');
                    for (const node of Array.from(nodesWithId)) {
                        const idValue = node.getAttribute('id');
                        const newIdValue = $idPrefix + idValue;
                        node.setAttribute('id', newIdValue);

                        // If this is symbol, should update existing size map.
                        if (node.tagName === 'symbol') {
                            const viewBoxAttr = node.getAttribute('viewBox');
                            if (viewBoxAttr) {
                                sizeMap[newIdValue] = parseViewBoxValue(viewBoxAttr);
                            }
                        }
                    }

                    // Update all gradient references.
                    const nodesWithUrl: NodeListOf<Element> = image.querySelectorAll('[fill^=url]');

                    for (const node of Array.from(nodesWithUrl)) {
                        const fill = extractFromUrl(node.getAttribute('fill'));
                        if (/^#/.test(fill)) {
                            node.setAttribute('fill', `url(#${$idPrefix + fill.substr(1)})`);
                        }
                    }

                    image.style.visibility = 'hidden';
                    image.style.position = 'fixed';
                    image.style.right = '0';
                    image.style.height = '1px';
                    image.style.width = '1px';

                    return {
                        image: $document.body.appendChild(image),
                        sizeMap,
                    };
                }),
                shareReplay(1)
            );
    }


    public readonly document: Document = inject(DOCUMENT);
    public readonly httpBackend: HttpBackend = inject(HttpBackend);
    public readonly httpClient: HttpClient;

    @Input({required: true})
    public readonly assetId: string;

    public idPrefix: string = '';

    public imageWidth: number = 0;
    public imageHeight: number = 0;
    public loaded: boolean = false;

    protected constructor() {
        this.httpClient = new HttpClient(this.httpBackend);
    }

    ngOnInit() {
        const proto = Object.getPrototypeOf(this);
        if (!SpriteImageBaseComponent.loaderMap.has(Object.getPrototypeOf(this))) {
            const idPrefix: string = 'spr' + SpriteImageBaseComponent.counter++ + '-';
            SpriteImageBaseComponent.loaderMap.set(
                proto,
                {
                    // NOTE: There are several bugs in different browsers when using `_` symbol in id.
                    prefix: idPrefix,
                    result: SpriteImageBaseComponent.initialize(this.httpClient, this.document, idPrefix, this.imageUrl, this.imageClass),
                }
            );
        }

        const initMapVO = SpriteImageBaseComponent.loaderMap.get(proto);

        this.idPrefix = initMapVO.prefix;

        initMapVO.result
            .subscribe($ => {
                // TODO: This should be updated dynamically eac time asset id is changed.
                const size = $.sizeMap[this.idPrefix + this.assetId];
                this.imageWidth = (size && size.width) || 0;
                this.imageHeight = (size && size.height) || 0;
                this.loaded = true;
            });
    }

    public get imageClass(): string {
        return 'hidden';
    }

    public readonly abstract imageUrl: string;

    public get href(): string {
        return '#' + this.idPrefix + this.assetId;
    }
}
